/**
 * Settings landing. Reachable by every role (the nav `/settings` entry).
 * Most settings surfaces are owner-locked later sprints, so this stays a
 * reserved placeholder for non-admins — EXCEPT it now exposes the
 * admin-only settings that shipped:
 *   • Staff Roles (its own admin-gated route)
 *   • Payment mode — the org default for money-before-delivery vs
 *     work-first (owner directive 2026-05-19). Edited inline here (single
 *     settings doc; no separate route needed, unlike Staff Roles).
 *
 * The admin-only Staff Roles screen itself is route-gated in App.tsx
 * (roles=['admin']); this landing only RENDERS the entries for admins so a
 * non-admin never even sees the affordance.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Box, Card, Btn3, EmptyNote, Tag } from '../components/ui';
import {
  listenPaymentSettings,
  setGlobalPaymentMode,
  PAYMENT_MODE_OPTIONS,
  paymentModeLabel,
  type PaymentMode,
} from '../lib/paymentMode';

export default function Settings() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';

  return (
    <Box title="Settings" note={isAdmin ? 'Admin' : 'Sprint 1 — surface reserved'}>
      {isAdmin ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <b>Staff roles</b>
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--ink-soft)',
                    marginTop: 4,
                  }}
                >
                  Assign each staffer's AVBP role (Admin / Staff / HR /
                  Partner / External consultant). Applies on their next
                  login.
                </div>
              </div>
              <span style={{ marginLeft: 'auto' }}>
                <Link to="/settings/staff-roles">
                  <Btn3 intent="ink" size="sm">
                    Open
                  </Btn3>
                </Link>
              </span>
            </div>
          </Card>

          <Card>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <b>Rate card</b>
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--ink-soft)',
                    marginTop: 4,
                  }}
                >
                  Edit each service's client-facing name, INR / USD base rate,
                  HSN, deliverables and default milestone template. L3B is
                  formula-priced. Saves to the services catalogue.
                </div>
              </div>
              <span style={{ marginLeft: 'auto' }}>
                <Link to="/settings/rate-card">
                  <Btn3 intent="ink" size="sm">
                    Open
                  </Btn3>
                </Link>
              </span>
            </div>
          </Card>

          <Card>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <b>Portal access</b>
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--ink-soft)',
                    marginTop: 4,
                  }}
                >
                  Control what each portal role can see (project status, floor
                  plan, report, payment details, etc.). Per-project overrides
                  available on each project's Access tab.
                </div>
              </div>
              <span style={{ marginLeft: 'auto' }}>
                <Link to="/settings/portal-access">
                  <Btn3 intent="ink" size="sm">
                    Open
                  </Btn3>
                </Link>
              </span>
            </div>
          </Card>

          <Card>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <b>WhatsApp · Canned messages</b>
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--ink-soft)',
                    marginTop: 4,
                  }}
                >
                  Create and edit the quick-reply canned messages your team
                  drops into WhatsApp chats (English + हिन्दी, in-window). The
                  library lives in the Team Inbox settings — this opens it
                  directly.
                </div>
              </div>
              <span style={{ marginLeft: 'auto' }}>
                <Link to="/messages?settings=canned">
                  <Btn3 intent="ink" size="sm">
                    Open
                  </Btn3>
                </Link>
              </span>
            </div>
          </Card>

          <PaymentModeCard />
        </div>
      ) : (
        <EmptyNote title="Settings land in a later sprint">
          The design-system foundation, role-based shell and Gmail/Apple
          auth are live. The settings you can change land when their sprint
          is authorised.
        </EmptyNote>
      )}
    </Box>
  );
}

/**
 * The org-default payment mode (settings/avbp_payment). Per-project
 * overrides live on ProjectDetail; this is the fallback when a project has
 * no explicit mode. Default = 'payment_before' (the locked behaviour) until
 * an admin changes it.
 */
function PaymentModeCard() {
  const { user } = useAuth();
  const { toast } = useToast();
  const actorName =
    user?.displayName || user?.email?.split('@')[0] || 'Admin';

  const [mode, setMode] = useState<PaymentMode | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsub = listenPaymentSettings(
      setMode,
      () => toast('Could not load payment settings (check connection)'),
    );
    return unsub;
  }, [toast]);

  async function save(next: PaymentMode) {
    if (busy || next === mode) return;
    setBusy(true);
    try {
      await setGlobalPaymentMode(next, actorName);
      toast(`Org default payment mode → ${paymentModeLabel(next)}.`);
    } catch (e) {
      toast(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <b>Payment mode (org default)</b>
        {mode && (
          <Tag kind={mode === 'work_first' ? 'warn' : 'val'}>
            {paymentModeLabel(mode)}
          </Tag>
        )}
      </div>
      <p
        style={{
          fontSize: 12.5,
          color: 'var(--ink-soft)',
          margin: '8px 0 12px',
          lineHeight: 1.55,
        }}
      >
        The default for every project. A project can override this on its
        own detail screen. <b>Work-first</b> means payment is still tracked
        and shown — it just never blocks a stage or delivery. Curation and
        quality gates always apply regardless of this setting.
      </p>
      {mode === null ? (
        <EmptyNote title="Loading payment settings…">
          Reading the org default.
        </EmptyNote>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PAYMENT_MODE_OPTIONS.map((o) => {
            const on = o.mode === mode;
            return (
              <Card key={o.mode} flat className="row-card">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <b style={{ fontSize: 13.5 }}>{o.label}</b>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--ink-soft)',
                        marginTop: 3,
                        lineHeight: 1.5,
                      }}
                    >
                      {o.blurb}
                    </div>
                  </div>
                  <span style={{ alignSelf: 'center' }}>
                    {on ? (
                      <Tag kind="val">current</Tag>
                    ) : (
                      <Btn3
                        intent="ghost"
                        size="sm"
                        onClick={() => save(o.mode)}
                        disabled={busy}
                      >
                        {busy ? 'Saving…' : 'Set as default'}
                      </Btn3>
                    )}
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Card>
  );
}
